import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import type { FreeShippingDelivery as FreeShippingAndReturnSchema } from '@amplience/content-types/typings/c-shipping-section';
import { storeKeyMapper } from '@shared/utils';
import { renderDeliveryOptionsTranslation, transformTextToId } from '@storefront/lib-global';
import { type DeliveryOption, DeliveryOptionsScope, type Variant } from '@storefront/lib-global/codegen/types';
import {
  useCartContext,
  useFeatureFlagsContext,
  useLocaleContext,
  useMasterLayoutContext,
} from '@storefront/lib-global/context';
import { RenderPrice } from '@storefront/lib-global/globalComponents/cart/taxAndTotals';
import { ShippingSection } from '@storefront/lib-global/globalComponents/shippingSection';
import { useGetDeliveryOptionsForProductsLazyQuery } from '@storefront/lib-global/graphqlDocument/deliveryMethods/getDeliveryOptionsForProducts.generated';
import { useStar2 } from '@storefront/lib-global/hooks';
import { PdpArrowDown } from '@storefront/lib-global/icons';
import { sendAccordionClickEvent } from '@storefront/lib-global/utils/gtm/events/accordion_click/sendAccordionClickEvent';
import { getCtLocale } from '@storefront/lib-global/utils/internationalization';
import { useSizeAndWidthContext } from '../../../context/sizeAndWidthContext';
import { useGetLineHeight } from '../../../hooks';
import {
  AccordionHeading,
  Container,
  FreeShippingAccordion,
  ShippingDays,
  ShippingMethodName,
  ShippingMethodsContainer,
  ShippingMethodsItem,
  ShippingMethodsList,
} from './FreeShippingAndReturns.styles';

type FreeShippingAndReturnsProps = {
  variants?: Variant[];
};

export function FreeShippingAndReturns({ variants }: FreeShippingAndReturnsProps): ReactElement | null {
  const { masterLayoutData } = useMasterLayoutContext();
  const { locale } = useLocaleContext();
  const { t } = useTranslation(['lib-products-common']);
  const { freeShippingAndReturn } = { ...masterLayoutData };
  const { selectedSku } = useSizeAndWidthContext();
  const { cartId, anonymousId } = useCartContext();
  const { isAdyenCheckoutEnabled } = useFeatureFlagsContext();
  const { isStar2 } = useStar2();

  const [isFreeShippingOpen, setIsFreeShippingOpen] = useState(true);
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>();
  const [currentSku, setCurrentSku] = useState('');
  const [displayDeliveryOptions, setDisplayDeliveryOptions] = useState(true);
  const textElRef = useRef<HTMLSpanElement>(null);
  const lineHeight = useGetLineHeight(textElRef);
  const destinationCountry = isStar2 ? locale?.split('-')[1].toUpperCase() : false;

  const handleToggle = () => {
    if (!isFreeShippingOpen) {
      sendAccordionClickEvent('Free Shipping and Returns');
    }
    setIsFreeShippingOpen((prev) => !prev);
  };

  const [getDeliveryOptionsData] = useGetDeliveryOptionsForProductsLazyQuery({
    variables: {
      input: {
        cartId,
        anonymousId,
        storeKey: storeKeyMapper(locale),
        locale: getCtLocale(locale),
        scope: DeliveryOptionsScope.PDP,
        skus: currentSku && isFreeShippingOpen ? [currentSku] : (variants?.map((variant) => variant.sku) as string[]),
        ...(destinationCountry ? { destinationCountry } : {}),
      },
    },
    onCompleted: ({ getDeliveryOptionsForProducts }) => {
      const nonNullOptions = getDeliveryOptionsForProducts?.deliveryOptions.flatMap((option) => option ?? []);

      const sortedDeliveryOptions = nonNullOptions?.slice()?.sort((a, b) => a.deliveryPriority - b.deliveryPriority);

      setDeliveryOptions(sortedDeliveryOptions);
      setDisplayDeliveryOptions(!!getDeliveryOptionsForProducts?.deliveryOptions.length);
    },
    onError: () => setDeliveryOptions([]),
  });

  useEffect(() => {
    if (isFreeShippingOpen) {
      getDeliveryOptionsData();
    }
  }, [getDeliveryOptionsData, isFreeShippingOpen, currentSku]);

  useEffect(() => {
    setIsFreeShippingOpen(false);
    setCurrentSku(selectedSku);
  }, [selectedSku, setCurrentSku, setIsFreeShippingOpen]);

  const shippingLabel = t('pdp.fsr.header');

  if (!displayDeliveryOptions) {
    return null;
  }

  return (
    <Container>
      <hr />
      <AccordionHeading>
        <FreeShippingAccordion
          aria-label={shippingLabel}
          type="button"
          onClick={handleToggle}
          $lineHeight={lineHeight}
          $isFreeShippingOpen={isFreeShippingOpen}
          aria-expanded={isFreeShippingOpen}
          aria-controls={`accordion-container-${transformTextToId(shippingLabel)}`}
        >
          <span ref={textElRef}>{shippingLabel}</span>
          <PdpArrowDown aria-hidden className="accordion-arrow-down" />
        </FreeShippingAccordion>
      </AccordionHeading>
      <ShippingMethodsContainer
        $isFreeShippingOpen={isFreeShippingOpen}
        id={`accordion-container-${transformTextToId(shippingLabel)}`}
        hidden={!isFreeShippingOpen}
        aria-hidden={!isFreeShippingOpen}
      >
        <ShippingMethodsList $isExpanded>
          {deliveryOptions?.map(({ currency, deliveryTime, name, price, key: deliveryKey }) => (
            <ShippingMethodsItem key={deliveryKey}>
              <RenderPrice currency={currency} price={price} />{' '}
              <ShippingMethodName data-testid="shippingMethodName">
                {isStar2 && !isAdyenCheckoutEnabled ? renderDeliveryOptionsTranslation(deliveryKey, t, name) : name}
              </ShippingMethodName>{' '}
              <ShippingDays data-testid="shippingDays">
                {isStar2 && !isAdyenCheckoutEnabled ? t('pdp.fsr.deliveryTime', { deliveryTime }) : deliveryTime}
              </ShippingDays>
            </ShippingMethodsItem>
          ))}
        </ShippingMethodsList>
        <ShippingSection {...(freeShippingAndReturn as unknown as FreeShippingAndReturnSchema)} />
      </ShippingMethodsContainer>
    </Container>
  );
}
